/**
 * Two pieces of reference material the wizard reads out rather than asks about.
 *
 * Neither is an input. The disclosure table turns a compliance question the client would otherwise
 * have to research into something they just read, and the variable bank is what stops anybody
 * having to know the template syntax — they pick a name, we write the `{{…}}`.
 */

export interface MarketDisclosure {
	/** ISO 3166-1 alpha-2. */
	market: string;
	recording: "Required" | "Recommended";
	aiDisclosure: "Required" | "Recommended";
	/** The local wall-clock window the market permits outbound calling in. */
	callingHours: string;
}

/**
 * What each market requires, as we maintain it. Reference, not input — the client cannot change a
 * row here, and a market we have not researched is simply absent rather than guessed at.
 */
export const MARKET_DISCLOSURES: MarketDisclosure[] = [
	{ market: "SG", recording: "Required", aiDisclosure: "Required", callingHours: "09:00–21:00" },
	{ market: "AU", recording: "Required", aiDisclosure: "Required", callingHours: "09:00–20:00" },
	{ market: "MY", recording: "Required", aiDisclosure: "Recommended", callingHours: "09:00–21:00" },
	{ market: "TH", recording: "Required", aiDisclosure: "Recommended", callingHours: "08:00–20:00" },
];

export interface TemplateVariable {
	/** The bare name. The picker wraps it in braces so the client never types them. */
	name: string;
	help: string;
}

export interface TemplateVariableGroup {
	title: string;
	variables: TemplateVariable[];
}

/**
 * The variables resolved per lead when a conversion email goes out.
 *
 * Every column of an uploaded lead list is addressable this way — the summary processor spreads
 * contact attributes into the same `{{variable}}` engine the agent prompts use — so `sales_rep`
 * and `sales_manager` are here without anyone configuring a mapping. These are the ones we know
 * are always present; a list with its own columns adds more.
 */
export const TEMPLATE_VARIABLE_GROUPS: TemplateVariableGroup[] = [
	{
		title: "From your lead list",
		variables: [
			{ name: "contact__sales_rep", help: "The rep who owns this contact." },
			{ name: "contact__sales_manager", help: "Their manager. Usually the BCC." },
			{ name: "contact__company", help: "The company the contact works for." },
			{ name: "contact__name", help: "The person we spoke to." },
			{ name: "contact__phone", help: "The number we dialled." },
		],
	},
	{
		title: "From the call",
		variables: [
			{ name: "lead__disposition", help: "The outcome recorded — this is what differentiates one email from another." },
			{ name: "lead__summary", help: "What the agent understood from the conversation." },
			{ name: "call__url", help: "A link to the recording." },
			{ name: "last_call__duration", help: "How long the call ran." },
			{ name: "last_call__dateTime", help: "When it happened, in the market's local time." },
		],
	},
	{
		title: "General",
		variables: [
			{ name: "campaign__title", help: "The campaign name from step 1." },
			{ name: "system__date", help: "Today's date when the email is sent." },
			{ name: "system__time", help: "The time the email is sent." },
		],
	},
];
